/**
 * Auth-Store — Hybrid:
 *  - login/logout/checkAuth/registerWithPassword → NestJS (apps/api) via useApi()
 *  - register(email/phone) + verify + forgotPassword/resetPassword → Nuxt-Server (Code-Flow)
 *
 * Beide Token-Welten teilen den localStorage-Key 'auth_token'. Voraussetzung:
 * Der User existiert in beiden DBs (Seed migriert User aus moebix → moebixnest).
 */
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useWishlistStore } from '~/stores/wishlist';

export interface User {
  id: string;
  email: string;
  name: string | null;
  avatar?: string | null;
  role?: string;
}

interface AuthResponse {
  user: User;
  token: string;
}

interface ApiResponse {
  message: string;
}

const TOKEN_STORAGE_KEY = 'auth_token';

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null);
  const token = ref<string | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);

  // ── Getters ──────────────────────────────────────────────────────
  const isAuthenticated = computed(() => !!token.value && !!user.value);
  const isAdmin = computed(() => user.value?.role === 'ADMIN');
  const isRedaktion = computed(() => user.value?.role === 'REDAKTION');
  const isPartner = computed(() => user.value?.role === 'PARTNER');
  const isDev = computed(() => user.value?.role === 'DEV');
  const hasAdminAccess = computed(() =>
    ['ADMIN', 'DEV', 'REDAKTION', 'PARTNER'].includes(user.value?.role ?? ''),
  );

  // ── Helpers ──────────────────────────────────────────────────────
  const setUser = (u: User) => {
    user.value = u;
  };

  const setToken = (t: string) => {
    token.value = t;
    if (typeof window !== 'undefined') localStorage.setItem(TOKEN_STORAGE_KEY, t);
  };

  const clearAuth = () => {
    user.value = null;
    token.value = null;
    error.value = null;
    if (typeof window !== 'undefined') localStorage.removeItem(TOKEN_STORAGE_KEY);
  };

  const setError = (msg: string | null) => {
    error.value = msg;
  };

  /** Liefert den Server-Error oder fallback. */
  const errorMessage = (err: unknown, fallback: string): string => {
    const e = err as { data?: { message?: string }; message?: string };
    return e?.data?.message ?? e?.message ?? fallback;
  };

  // ── NestJS-backed Actions ────────────────────────────────────────

  /** Login gegen NestJS — setzt Cookie (HttpOnly) + liefert Bearer-Token als Fallback. */
  const login = async (email: string, password: string) => {
    loading.value = true;
    error.value = null;
    try {
      const api = useApi();
      const response = await api<AuthResponse>('/api/auth/login', {
        method: 'POST',
        body: { email, password },
      });
      if (response.user && response.token) {
        setUser(response.user);
        setToken(response.token);
        const wishlist = useWishlistStore();
        await wishlist.syncWithServer();
        return { success: true };
      }
      throw new Error('Login fehlgeschlagen');
    } catch (err: unknown) {
      const msg = errorMessage(err, 'Anmeldung fehlgeschlagen');
      setError(msg);
      return { success: false, error: msg };
    } finally {
      loading.value = false;
    }
  };

  /** Direkte Email-Password-Registrierung gegen NestJS (für Admin/Dev-Accounts). */
  const registerWithPassword = async (
    email: string,
    password: string,
    name?: string,
  ) => {
    loading.value = true;
    error.value = null;
    try {
      const api = useApi();
      const response = await api<AuthResponse>('/api/auth/register', {
        method: 'POST',
        body: { email, password, name },
      });
      if (response.user && response.token) {
        setUser(response.user);
        setToken(response.token);
        return { success: true };
      }
      throw new Error('Registrierung fehlgeschlagen');
    } catch (err: unknown) {
      const msg = errorMessage(err, 'Registrierung fehlgeschlagen');
      setError(msg);
      return { success: false, error: msg };
    } finally {
      loading.value = false;
    }
  };

  /** Logout — räumt Cookie serverseitig + lokalen State auf. */
  const logout = async () => {
    loading.value = true;
    try {
      const wishlist = useWishlistStore();
      wishlist.resetSyncState();
      const api = useApi();
      await api('/api/auth/logout', { method: 'POST' });
    } catch (err: unknown) {
      console.warn('Logout-API failed:', err);
    } finally {
      clearAuth();
      loading.value = false;
      await navigateTo('/');
    }
  };

  /** Initial bei App-Start: Token aus Storage holen + gegen NestJS validieren. */
  const checkAuth = async (): Promise<boolean> => {
    if (typeof window === 'undefined') return false;
    const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!stored) return false;
    token.value = stored;
    try {
      const api = useApi();
      const response = await api<{ user: User }>('/api/auth/me');
      if (response.user) {
        setUser(response.user);
        const wishlist = useWishlistStore();
        await wishlist.fetchFromServer();
        return true;
      }
      clearAuth();
      return false;
    } catch (err: unknown) {
      console.warn('Token-Verification failed:', err);
      clearAuth();
      return false;
    }
  };

  // ── Code-Verification-Flow ─────────────────────────────────────────

  const register = async (email?: string, phone?: string) => {
    loading.value = true;
    error.value = null;
    try {
      const api = useApi();
      const response = await api<{
        success: boolean;
        message: string;
        user: User;
        devCode?: string;
      }>('/api/auth/register', {
        method: 'POST',
        body: { email, phone },
      });
      if (response.success) {
        return {
          success: true,
          needsVerification: true,
          message: response.message,
          devCode: response.devCode,
        };
      }
      throw new Error('Registrierung fehlgeschlagen');
    } catch (err: unknown) {
      const msg = errorMessage(err, 'Registrierung fehlgeschlagen');
      setError(msg);
      return { success: false, error: msg };
    } finally {
      loading.value = false;
    }
  };

  const verify = async (
    email: string | undefined,
    phone: string | undefined,
    code: string,
  ) => {
    loading.value = true;
    error.value = null;
    try {
      const api = useApi();
      const response = await api<AuthResponse>('/api/auth/verify', {
        method: 'POST',
        body: { email, phone, code },
      });
      if (response.user && response.token) {
        setUser(response.user);
        setToken(response.token);
        return { success: true };
      }
      throw new Error('Verifizierung fehlgeschlagen');
    } catch (err: unknown) {
      const msg = errorMessage(err, 'Verifizierung fehlgeschlagen');
      setError(msg);
      return { success: false, error: msg };
    } finally {
      loading.value = false;
    }
  };

  const forgotPassword = async (email: string) => {
    loading.value = true;
    error.value = null;
    try {
      const api = useApi();
      const response = await api<ApiResponse>('/api/auth/forgot-password', {
        method: 'POST',
        body: { email },
      });
      return {
        success: true,
        message: response.message ?? 'Passwort-Reset E-Mail wurde versendet',
      };
    } catch (err: unknown) {
      const msg = errorMessage(err, 'Passwort-Reset fehlgeschlagen');
      setError(msg);
      return { success: false, error: msg };
    } finally {
      loading.value = false;
    }
  };

  const resetPassword = async (resetToken: string, newPassword: string) => {
    loading.value = true;
    error.value = null;
    try {
      const api = useApi();
      const response = await api<ApiResponse>('/api/auth/reset-password', {
        method: 'POST',
        body: { token: resetToken, newPassword },
      });
      return {
        success: true,
        message: response.message ?? 'Passwort wurde erfolgreich zurückgesetzt',
      };
    } catch (err: unknown) {
      const msg = errorMessage(err, 'Passwort-Reset fehlgeschlagen');
      setError(msg);
      return { success: false, error: msg };
    } finally {
      loading.value = false;
    }
  };

  return {
    user,
    token,
    loading,
    error,
    isAuthenticated,
    isAdmin,
    isRedaktion,
    isPartner,
    isDev,
    hasAdminAccess,
    login,
    register,
    registerWithPassword,
    verify,
    logout,
    forgotPassword,
    resetPassword,
    checkAuth,
    setError,
    clearAuth,
  };
});
